import {Auth0Interceptor} from './services/auth/identity/auth0.interceptor';
import {Auth0Guard} from './guards/auth0.guard';
import {APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideZoneChangeDetection} from '@angular/core';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {
    provideRouter,
    RouteReuseStrategy,
    withComponentInputBinding,
    withInMemoryScrolling,
    withRouterConfig
} from '@angular/router';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {provideNativeDateAdapter} from '@angular/material/core';

// Third-party Modules
import {MaterialModule} from './material.module';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {provideTablerIcons} from './shared/tabler-icons/tabler-icons';
import * as TablerIcons from './shared/tabler-icons/tabler-icon-set';

// MSAL Imports
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptorConfiguration,
    MsalModule,
    MsalService
} from '@azure/msal-angular';
import {
    BrowserCacheLocation,
    InteractionType,
    IPublicClientApplication,
    LogLevel,
    PublicClientApplication
} from '@azure/msal-browser';
import {environment} from '../environments/environment';
import {loginRequest, protectedResources} from './auth.config';

// Shine Auth Imports
import {ShineAuthInterceptor} from './services/auth/shine-auth/shine-auth.interceptor';
import {ShineAuthService} from './services/auth/shine-auth/shine-auth.service';
import {ShineAuthGuard} from './services/auth/shine-auth/shine-auth.guard';
import {ShineAuthClient} from './services/auth/shine-auth/shine-auth-client';
import {EnhancedMsalInterceptor} from './services/auth/msal/enhanced-msal.interceptor';
import {EnhancedMsalGuard} from './services/auth/msal/enhanced-msal.guard';
import {ShineUserInfoService} from './services/auth/shine-auth/shine-user-info.service';

import {routes} from './app.routes';
import {CustomRouteReuseStrategy} from "./services/CustomRouteReuse.strategy";

// Helper to decide interaction type based on browser
function getPreferredInteractionType(): InteractionType.Redirect {
  const storageKey = 'shine_msal_interaction';

  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storageKey, 'redirect');
    }
  } catch {
    // ignore storage access issues (SSR or privacy mode)
  }

  return InteractionType.Redirect;
}

// MSAL factory functions
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.azureAdClientId,
      authority: environment.azureAdAuthority,
      redirectUri: window.location.origin
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Info, // LogLevel.Error
        piiLoggingEnabled: false
      }
    }
  });
}

export function initializeMsal(instance: IPublicClientApplication): () => Promise<void> {
    return () => instance.initialize();
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  // Add protected resources
  protectedResourceMap.set(protectedResources['api'].endpoint, protectedResources['api'].scopes);
  protectedResourceMap.set(protectedResources['enrich'].endpoint, protectedResources['enrich'].scopes);
  protectedResourceMap.set(protectedResources['auth'].endpoint, protectedResources['auth'].scopes);
  protectedResourceMap.set(protectedResources['connect'].endpoint, protectedResources['connect'].scopes);
    protectedResourceMap.set(protectedResources['webinars'].endpoint, protectedResources['webinars'].scopes);
  protectedResourceMap.set(protectedResources['graph'].endpoint, protectedResources['graph'].scopes);

  return {
    interactionType: getPreferredInteractionType(),
    protectedResourceMap
  };
}

export function MsalGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: getPreferredInteractionType(),
    authRequest: loginRequest
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideNativeDateAdapter(),
    provideRouter(
        routes,
        withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
        withComponentInputBinding(),
        withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    // Add the custom route reuse strategy:
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    importProvidersFrom(
        FormsModule,
        ReactiveFormsModule,
        MaterialModule,
        ...(environment.msalEnabled ? [MsalModule] : [])
    ),
      ...provideTablerIcons(TablerIcons),

    // MSAL providers (if MSAL is enabled)
    ...(environment.msalEnabled ? [
      {provide: HTTP_INTERCEPTORS, useClass: EnhancedMsalInterceptor, multi: true},
      {provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory},
        {provide: APP_INITIALIZER, useFactory: initializeMsal, deps: [MSAL_INSTANCE], multi: true},
      {provide: MSAL_GUARD_CONFIG, useFactory: MsalGuardConfigFactory},
      {provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory},
      MsalService,
      MsalGuard,
      MsalBroadcastService,
      EnhancedMsalGuard
    ] : []),

    // Shine Auth providers
    ...(environment.authenticationProvider === 'auth0' ? [
      {provide: HTTP_INTERCEPTORS, useClass: Auth0Interceptor, multi: true},
      {provide: EnhancedMsalGuard, useClass: Auth0Guard},
    ] : [{provide: HTTP_INTERCEPTORS, useClass: ShineAuthInterceptor, multi: true}]),
    ShineAuthClient,
    ShineAuthService,
    ShineAuthGuard,
    ShineUserInfoService
  ],
};
